import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { STATUS_CHIP, STATUS_LABELS } from '../../lib/eventWorkspace';
import { inAppEventWhen, mergeRegistrationEvents, yesterdayInZone } from '../../lib/formEventRegistration';
import { cn } from '../../lib/utils';
import { INPUT } from '../../lib/formConstants';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const DEBOUNCE_MS = 250;
const PAGE_SIZE = 100;
// `list_events` filters on the STORED status, so the two states an owner may register on are two reads. Legacy
// spellings of "published" (`scheduled`, `live`, ...) are not listed; a stored one still displays (read by id).
const STATUSES = ['published', 'draft'];

const asList = (payload) => (Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []));

const statusKey = (event) => (STATUS_LABELS[event?.status] ? event.status : 'draft');

/**
 * The event combobox for a form's "Event registration" setting (#919, E18).
 *
 * It lists UPCOMING draft and published events, soonest first, and never takes free text: Enter picks only a highlighted
 * option, and a typed query only narrows the list (the server's `search`, debounced 250 ms). `EventSelect` (campaign
 * triggers) lists everything and stores typed text as an id, which is why it is not reused.
 *
 * `value` is the stored event id. A stored event that is not in the list (past, beyond the first 100, or a legacy
 * status) is read once by id so its name still shows. `onSelect(event)` hands over the whole event row.
 *
 * Typed text narrows the list AT ONCE, in the browser, by the same name match the server's `search` does, and the debounced
 * read then refreshes it. So ArrowDown and Enter typed right after the text act on the narrowed list, and the highlight
 * (kept by event id) survives the refresh.
 *
 * The read effects take `getAuthHeader` through a ref: a silent token refresh must not refetch the list under an open
 * listbox, and the header is read fresh on every request anyway.
 */
export default function RegistrationEventPicker({ value, onSelect, id, disabled = false, 'aria-describedby': describedBy }) {
    const { getAuthHeader } = useAuth() || {};
    const authRef = useRef(getAuthHeader);
    authRef.current = getAuthHeader;
    const listboxId = useId();
    const optionId = (index) => `${listboxId}-option-${index}`;

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    const [activeId, setActiveId] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [options, setOptions] = useState([]);
    const [reloadKey, setReloadKey] = useState(0);
    // Every event this picker has seen, by id, so the selection keeps its name when a search filters it out.
    const [known, setKnown] = useState({});
    const [missing, setMissing] = useState({});
    const byIdRequested = useRef(new Set());
    const mounted = useRef(true);
    const listRef = useRef(null);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    // Typed text reaches the server 250 ms after the last keystroke. Resetting the query to '' (Esc, a pick) comes back
    // to the same `search`, so state bails out and nothing refetches.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(query.trim()), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        const now = new Date();
        const dateFrom = yesterdayInZone(now);
        const params = { page_size: PAGE_SIZE, ...(dateFrom ? { date_from: dateFrom } : {}), ...(search ? { search } : {}) };
        const headers = authRef.current?.() || {};
        Promise.all(STATUSES.map((state) => axios.get(`${API}/events`, { params: { ...params, status: state }, headers })))
            .then((responses) => {
                if (cancelled) return;
                const merged = mergeRegistrationEvents(responses.map((response) => asList(response?.data)), now);
                setOptions(merged);
                setKnown((previous) => ({ ...previous, ...Object.fromEntries(merged.map((event) => [event.event_id, event])) }));
                setStatus('ready');
            })
            .catch(() => { if (!cancelled) setStatus('error'); });
        return () => { cancelled = true; };
    }, [search, reloadKey]);

    // A stored event the list does not carry is read ONCE by id, after the list has settled so a listed one costs nothing.
    useEffect(() => {
        if (!value || status === 'loading' || known[value] || byIdRequested.current.has(value)) return;
        byIdRequested.current.add(value);
        axios.get(`${API}/events/${encodeURIComponent(value)}`, { headers: authRef.current?.() || {} })
            .then((response) => {
                if (!mounted.current) return;
                const event = response?.data;
                if (event && typeof event === 'object' && !Array.isArray(event)) {
                    setKnown((previous) => ({ ...previous, [value]: { ...event, event_id: event.event_id || value } }));
                } else {
                    setMissing((previous) => ({ ...previous, [value]: true }));
                }
            })
            .catch(() => { if (mounted.current) setMissing((previous) => ({ ...previous, [value]: true })); });
    }, [value, status, known]);

    const typed = query.trim();
    const needle = typed.toLowerCase();
    // The rows on screen: the last read, narrowed by what is typed now. They stay up while a newer read is on its way;
    // after a failed read they are gone, so the error stands alone.
    const shown = useMemo(() => {
        if (status === 'error') return [];
        return needle ? options.filter((event) => String(event.name || '').toLowerCase().includes(needle)) : options;
    }, [options, needle, status]);
    const activeIndex = activeId ? shown.findIndex((event) => event.event_id === activeId) : -1;
    // The typed text has not reached the server yet, or its read has not come back: what is listed may still change.
    const refreshing = status === 'loading' || typed !== search;

    useEffect(() => {
        if (!open || activeIndex < 0) return;
        const node = listRef.current?.children?.[activeIndex];
        if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
    }, [open, activeIndex]);

    const selected = value ? known[value] : null;
    const label = selected ? (selected.name || selected.event_id) : (value && missing[value] ? 'Event unavailable' : '');

    const close = () => {
        setOpen(false);
        setQuery('');
        setActiveId(null);
    };

    const choose = (event) => {
        close();
        onSelect?.(event);
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                const first = e.key === 'ArrowDown' ? shown[0] : shown[shown.length - 1];
                setActiveId(first ? first.event_id : null);
                return;
            }
            if (!shown.length) return;
            let next;
            if (e.key === 'ArrowDown') next = Math.min(activeIndex + 1, shown.length - 1);
            else next = activeIndex < 0 ? shown.length - 1 : Math.max(activeIndex - 1, 0);
            setActiveId(shown[next].event_id);
        } else if (e.key === 'Enter') {
            if (!open) return;
            e.preventDefault();
            // No highlighted option, no pick: the field never turns typed text into a value.
            if (activeIndex >= 0 && shown[activeIndex]) choose(shown[activeIndex]);
        } else if (e.key === 'Escape') {
            if (!open) return;
            e.preventDefault();
            e.stopPropagation();
            close();
        } else if (e.key === 'Tab') {
            close();
        }
    };

    return (
        <div className="relative w-full" data-testid="registration-event-picker">
            <div className="relative">
                <input
                    id={id}
                    type="text"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-haspopup="listbox"
                    aria-autocomplete="list"
                    aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
                    aria-describedby={describedBy}
                    autoComplete="off"
                    disabled={disabled}
                    value={open ? query : label}
                    placeholder="Search upcoming events…"
                    className={cn(INPUT, 'pr-8')}
                    onClick={() => setOpen(true)}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveId(null); }}
                    onKeyDown={onKeyDown}
                    onBlur={close}
                />
                <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            </div>
            {open && (
                // The mouse must not take focus from the input: a blur would close the panel before a click lands.
                <div className="absolute z-20 mt-1 w-full rounded-lg border border-stone-200 bg-white shadow-lg" onMouseDown={(e) => e.preventDefault()}>
                    <ul ref={listRef} role="listbox" id={listboxId} aria-label="Upcoming events" className="max-h-60 overflow-y-auto py-1">
                        {shown.map((event, index) => {
                            const state = statusKey(event);
                            const isSelected = event.event_id === value;
                            return (
                                <li
                                    key={event.event_id}
                                    id={optionId(index)}
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => choose(event)}
                                    onMouseMove={() => { if (activeId !== event.event_id) setActiveId(event.event_id); }}
                                    className={cn('cursor-pointer px-3 py-2', index === activeIndex && 'bg-stone-100')}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="flex min-w-0 items-center gap-1.5">
                                            <Check aria-hidden="true" className={cn('h-3.5 w-3.5 flex-shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                                            <span className="truncate text-sm font-medium text-stone-800">{event.name || event.event_id}</span>
                                        </span>
                                        <span className={cn('flex-shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium', STATUS_CHIP[state])}>
                                            {STATUS_LABELS[state]}
                                        </span>
                                    </div>
                                    <div className="truncate pl-5 text-xs text-stone-500">{inAppEventWhen(event)}</div>
                                </li>
                            );
                        })}
                    </ul>
                    {status !== 'error' && refreshing && (
                        <div role="status" className="flex items-center gap-2 px-3 py-3 text-sm text-stone-500">
                            <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> Loading events…
                        </div>
                    )}
                    {status === 'error' && (
                        <div className="flex items-center justify-between gap-2 px-3 py-3 text-sm text-stone-600">
                            <span>Events could not be loaded.</span>
                            <button type="button" onClick={() => setReloadKey((n) => n + 1)} className="font-medium text-stone-800 underline">
                                Retry
                            </button>
                        </div>
                    )}
                    {status === 'ready' && !refreshing && shown.length === 0 && (
                        <div className="px-3 py-3 text-sm text-stone-500">
                            {search
                                ? `No upcoming events match “${search}”.`
                                : (<>No upcoming events. <a href="/events" target="_blank" rel="noopener noreferrer" className="font-medium text-stone-800 underline">Create one in Events.</a></>)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
